import React from 'react';
import clsx from 'clsx';

interface GuestCountPanelProps {
  adults: number;
  children: number;
  maxCapacity: number;
  onAdultsChange: (count: number) => void;
  onChildrenChange: (count: number) => void;
  messages: {
    adultsLabel: string;
    childrenLabel: string;
    childrenDetail?: string;
    guestsLabel?: string;
    maxCapacityLabel?: string;
  };
}

/**
 * Guest count panel with adult/child counters.
 * Shows capacity warning when exceeding room capacity.
 */
export const GuestCountPanel: React.FC<GuestCountPanelProps> = ({
  adults,
  children,
  maxCapacity,
  onAdultsChange,
  onChildrenChange,
  messages,
}) => {
  const totalGuests = adults + children;
  const isOverCapacity = maxCapacity > 0 && totalGuests > maxCapacity;

  return (
    <div className="w-full">
      {/* Header with total and capacity */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            {messages.guestsLabel || 'Guests'}
          </h3>
          {maxCapacity > 0 && (
            <p className="text-sm text-gray-500">
              {messages.maxCapacityLabel || 'Max capacity'}: {maxCapacity}
            </p>
          )}
        </div>

        {/* Total badge */}
        <div
          className={clsx(
            'px-3 py-1.5 rounded-full text-sm font-semibold',
            isOverCapacity ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-700',
          )}
        >
          {totalGuests} {totalGuests === 1 ? 'guest' : 'guests'}
        </div>
      </div>

      {/* Capacity warning */}
      {isOverCapacity && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <WarningIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">
              You have more guests than the room capacity. Additional beds may be required.
            </p>
          </div>
        </div>
      )}

      {/* Counters */}
      <div className="space-y-4">
        {/* Adults */}
        <CounterRow
          label={messages.adultsLabel}
          value={adults}
          min={1}
          onChange={onAdultsChange}
        />

        {/* Children */}
        <CounterRow
          label={messages.childrenLabel}
          detail={messages.childrenDetail}
          value={children}
          min={0}
          onChange={onChildrenChange}
        />
      </div>
    </div>
  );
};

interface CounterRowProps {
  label: string;
  detail?: string;
  value: number;
  min: number;
  max?: number;
  onChange: (value: number) => void;
}

const CounterRow: React.FC<CounterRowProps> = ({
  label,
  detail,
  value,
  min,
  max = 20,
  onChange,
}) => {
  const canDecrease = value > min;
  const canIncrease = value < max;

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div>
        <p className="font-medium text-gray-900">{label}</p>
        {detail && <p className="text-sm text-gray-500">{detail}</p>}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => canDecrease && onChange(value - 1)}
          disabled={!canDecrease}
          className={clsx(
            'w-9 h-9 rounded-full flex items-center justify-center',
            'border-2 transition-all duration-150',
            'focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1',
            {
              'border-emerald-500 text-emerald-600 hover:bg-emerald-50': canDecrease,
              'border-gray-200 text-gray-300 cursor-not-allowed': !canDecrease,
            },
          )}
          aria-label={`Decrease ${label}`}
        >
          <MinusIcon className="w-4 h-4" />
        </button>

        <span className="w-8 text-center text-lg font-semibold text-gray-900">{value}</span>

        <button
          type="button"
          onClick={() => canIncrease && onChange(value + 1)}
          disabled={!canIncrease}
          className={clsx(
            'w-9 h-9 rounded-full flex items-center justify-center',
            'border-2 transition-all duration-150',
            'focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1',
            {
              'border-emerald-500 text-emerald-600 hover:bg-emerald-50': canIncrease,
              'border-gray-200 text-gray-300 cursor-not-allowed': !canIncrease,
            },
          )}
          aria-label={`Increase ${label}`}
        >
          <PlusIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Icon components
const MinusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
  </svg>
);

const PlusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

const WarningIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
    />
  </svg>
);

export default GuestCountPanel;
